/* RESPONSIBLE TEAM: team-automation-foundations */
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type FinGuidanceApplied from 'embercom/objects/inbox/renderable/fin-guidance-applied';
import type ConversationMarkedAsSpam from 'embercom/objects/inbox/renderable/conversation-marked-as-spam';
import { AiAgentEventType } from 'embercom/objects/inbox/renderable/ai-agent-event';
import type AiAgentRenderableEvent from 'embercom/objects/inbox/renderable/ai-agent-event';
import type IntlService from 'ember-intl/services/intl';
import { htmlToTextContent } from 'embercom/lib/html-unescape';

export function getSpamIndicatorText(
  spamMarkedBy: number | undefined,
  currentTeammateId: number | undefined,
  intl: IntlService,
  renderableParts: RenderablePart[],
): string {
  if (spamMarkedBy !== undefined) {
    if (spamMarkedBy === currentTeammateId) {
      return intl.t('inbox.conversation-stream.part-groups.user-content.you-spam-indicator');
    }
    let name = getSpamMarkerNameFromParts(renderableParts);
    return intl.t('inbox.conversation-stream.part-groups.user-content.teammate-spam-indicator', {
      name: name || intl.t('inbox.operator'),
    });
  }
  return intl.t('inbox.conversation-stream.part-groups.user-content.fin-spam-indicator');
}

export function getSpamGuidanceNamesFromParts(renderableParts: RenderablePart[]): string[] {
  let lastSpamEventIndex = -1;
  for (let i = renderableParts.length - 1; i >= 0; i--) {
    if (renderableParts[i].renderableType === RenderableType.ConversationMarkedAsSpam) {
      lastSpamEventIndex = i;
      break;
    }
  }

  let startIndex = lastSpamEventIndex === -1 ? renderableParts.length - 1 : lastSpamEventIndex - 1;

  let names: string[] = [];
  for (let i = startIndex; i >= 0; i--) {
    let part = renderableParts[i];
    if (part.renderableType === RenderableType.FinGuidanceApplied) {
      let data = part.renderableData as FinGuidanceApplied;
      let maxLength = 50;
      for (let guideline of data.guidelines) {
        let name = htmlToTextContent(guideline.title || guideline.text);
        if (name) {
          names.push(name.length > maxLength ? `${name.slice(0, maxLength)}...` : name);
        }
      }
    } else if (
      part.renderableType === RenderableType.ConversationMarkedAsSpam ||
      part.renderableType === RenderableType.ConversationUnmarkedAsSpam ||
      part.renderableType === RenderableType.UserComment ||
      part.renderableType === RenderableType.UserEmailComment
    ) {
      break;
    }
  }
  return names;
}

export function getSpamFlaggedEmailPartIndex(renderableParts: RenderablePart[]): number {
  let ignoringEventIndex = -1;
  for (let i = 0; i < renderableParts.length; i++) {
    let part = renderableParts[i];
    if (part.renderableType === RenderableType.AiAgentEvent) {
      let data = part.renderableData as AiAgentRenderableEvent;
      if (
        data.eventType === AiAgentEventType.AiAgentIgnoringUserMessage &&
        data.eventData?.[AiAgentEventType.AiAgentIgnoringUserMessage]?.reason ===
          'probable_junk_email'
      ) {
        ignoringEventIndex = i;
        break;
      }
    }
  }

  if (ignoringEventIndex === -1) {
    return -1;
  }

  for (let i = ignoringEventIndex - 1; i >= 0; i--) {
    if (renderableParts[i].renderableType === RenderableType.UserEmailComment) {
      return i;
    }
  }

  return -1;
}

function getSpamMarkerNameFromParts(renderableParts: RenderablePart[]): string | undefined {
  for (let i = renderableParts.length - 1; i >= 0; i--) {
    if (renderableParts[i].renderableType === RenderableType.ConversationMarkedAsSpam) {
      let data = renderableParts[i].renderableData as ConversationMarkedAsSpam;
      return data.adminSummary?.name;
    }
  }
  return undefined;
}
